import { isFeatureEnabled } from '../lib/features';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { getSite, listForms, type Site, type FormRow } from '../lib/db';
import { useUserOrg } from '../lib/org';
import { LoadingIndicator } from '../components/LoadingIndicator';

/**
 * Forms action menu — pick a form type to start.
 *
 * Three tile links (Toolbox talk / Accident / Subbie induction) sit at the
 * top. A clickable banner below — "Forms recorded on this site" — opens
 * the full history view at /sites/:siteId/forms/history. Kept deliberately
 * spare so the primary action (start a new form) is the focus.
 *
 * The history list lives in FormsHistory.tsx.
 */

const KINDS: { kind: FormRow['kind']; title: string; sub: string; featureKey?: 'subbies' }[] = [
  { kind: 'toolbox_talk', title: 'Toolbox talk', sub: 'Topic, why, attendees with signatures.' },
  { kind: 'accident', title: 'Accident / near-miss', sub: 'Log every incident — RIDDOR check built in.' },
  { kind: 'subcontractor_induction', title: 'Subbie induction', sub: 'Person, company, insurance, competence.', featureKey: 'subbies' },
];

export function FormsMenu() {
  const { org } = useUserOrg();
  const { siteId } = useParams<{ siteId: string }>();
  const [site, setSite] = useState<Site | null>(null);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!siteId) return;
    Promise.all([getSite(siteId), listForms(siteId, undefined, 500)])
      .then(([s, f]) => { setSite(s); setCount(f.length); })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [siteId]);

  if (loading) {
    return <AppShell title="Forms" back={`/sites/${siteId ?? ''}`}><LoadingIndicator /></AppShell>;
  }
  if (!site) {
    return <AppShell title="Forms" back={`/sites/${siteId ?? ''}`}><div className="banner banner-error">{error ?? 'Site not found.'}</div></AppShell>;
  }

  return (
    <AppShell title={`Forms · ${site.name}`} back={`/sites/${site.id}`}>
      {error && <div className="banner banner-error">{error}</div>}

      <h2 className="section-title">Start a new form</h2>
      <div>
        {KINDS.filter((k) => !k.featureKey || isFeatureEnabled(org, k.featureKey)).map((k) => (
          <Link key={k.kind} to={`/sites/${site.id}/forms/${k.kind}`} className="check-tile">
            <div className="check-tile-text">
              <p className="check-tile-title">{k.title}</p>
              <p className="check-tile-sub">{k.sub}</p>
            </div>
            <span className="chev">›</span>
          </Link>
        ))}
      </div>

      {count === 0 ? (
        <div className="card" style={{ textAlign: 'center', marginTop: 18 }}>
          <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
            No forms saved yet on this site. Start one above.
          </p>
        </div>
      ) : (
        <Link
          to={`/sites/${site.id}/forms/history`}
          className="rams-list-banner rams-list-banner-link"
          aria-label={`View all ${count} forms recorded on this site`}
          style={{ marginTop: 18 }}
        >
          <div>
            <span className="rams-list-banner-title">Forms recorded on this site</span>
            <span className="rams-list-banner-meta">{count} on file · tap to view or filter by type</span>
          </div>
          <span className="rams-list-banner-chev" aria-hidden="true">›</span>
        </Link>
      )}
    </AppShell>
  );
}
